/**
 * ExportSettingsModal Component
 * 
 * Configure export settings including disclaimer footer, branding, and format options.
 */

import React, { useState } from 'react';
import { 
  Download, 
  FileText, 
  Calendar, 
  Building2, 
  X, 
  Image as ImageIcon,
  Check
} from 'lucide-react';
import { 
  ExportConfig, 
  DisclaimerSettings, 
  DEFAULT_EXPORT_CONFIG,
  DEFAULT_DISCLAIMER_SETTINGS,
  DEFAULT_DISCLAIMER_TEXT
} from '../types';

interface ExportSettingsModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback to close the modal */
  onClose: () => void;
  /** Current export config */
  exportConfig: ExportConfig;
  /** Callback to update export config */
  onExportConfigChange: (config: ExportConfig) => void;
  /** Current disclaimer settings */
  disclaimerSettings: DisclaimerSettings;
  /** Callback to update disclaimer settings */
  onDisclaimerSettingsChange: (settings: DisclaimerSettings) => void;
  /** Callback to trigger export */
  onExport: () => void;
  /** Total area string to display in preview (e.g., "985 sq ft / 91 sqm") */
  totalAreaString?: string;
  /** Optional className */
  className?: string;
}

const ExportSettingsModal: React.FC<ExportSettingsModalProps> = ({
  isOpen,
  onClose,
  exportConfig,
  onExportConfigChange,
  disclaimerSettings,
  onDisclaimerSettingsChange,
  onExport,
  totalAreaString = '— sq ft / — sqm',
  className = ''
}) => {
  const [activeTab, setActiveTab] = useState<'format' | 'footer'>('footer');

  if (!isOpen) return null;

  // Preview the disclaimer text with area placeholder replaced
  const previewDisclaimer = disclaimerSettings.disclaimerText.replace(
    '{totalArea}',
    totalAreaString
  );

  const handleToggleFooter = () => {
    onExportConfigChange({
      ...exportConfig,
      includeFooter: !exportConfig.includeFooter
    });
  };

  const handleFormatChange = (format: 'png' | 'jpeg') => {
    onExportConfigChange({
      ...exportConfig,
      format
    });
  };

  const handleScaleChange = (scale: number) => {
    onExportConfigChange({
      ...exportConfig,
      scale
    });
  };

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm ${className}`}>
      <div className="bg-slate-900 rounded-2xl shadow-2xl border border-slate-700 w-full max-w-2xl mx-4 overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700 bg-slate-800/50 shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-teal-900/50 rounded-lg">
              <Download size={20} className="text-teal-400" />
            </div>
            <div>
              <h3 className="font-semibold text-white">Export Settings</h3>
              <p className="text-xs text-slate-400">Configure your floor plan export</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-700 shrink-0">
          <button
            onClick={() => setActiveTab('footer')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors
              ${activeTab === 'footer' 
                ? 'text-teal-400 border-b-2 border-teal-400 bg-slate-800/30' 
                : 'text-slate-400 hover:text-white'
              }`}
          >
            <FileText size={16} className="inline mr-2" />
            Disclaimer Footer
          </button>
          <button
            onClick={() => setActiveTab('format')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors
              ${activeTab === 'format' 
                ? 'text-teal-400 border-b-2 border-teal-400 bg-slate-800/30' 
                : 'text-slate-400 hover:text-white'
              }`}
          >
            <ImageIcon size={16} className="inline mr-2" />
            Format & Quality
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'footer' && (
            <div className="space-y-6">
              {/* Enable footer toggle */}
              <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-xl border border-slate-700">
                <div>
                  <h4 className="font-medium text-white">Include Disclaimer Footer</h4>
                  <p className="text-xs text-slate-400 mt-1">
                    Adds a professional footer with area and disclaimer
                  </p>
                </div>
                <button
                  onClick={handleToggleFooter}
                  className={`relative w-12 h-6 rounded-full transition-colors
                    ${exportConfig.includeFooter ? 'bg-teal-600' : 'bg-slate-600'}`}
                >
                  <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform
                    ${exportConfig.includeFooter ? 'left-7' : 'left-1'}`} />
                </button>
              </div>

              {exportConfig.includeFooter && (
                <>
                  {/* Property Name */}
                  <div>
                    <label className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-2 block">
                      Property Name (Optional)
                    </label>
                    <input
                      type="text"
                      value={disclaimerSettings.propertyName || ''}
                      onChange={(e) => onDisclaimerSettingsChange({
                        ...disclaimerSettings,
                        propertyName: e.target.value || undefined
                      })}
                      placeholder="e.g., 5 Cherry Walk"
                      className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg
                        text-white placeholder-slate-500 focus:outline-none focus:border-teal-500"
                    />
                  </div>

                  {/* Options row */}
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={disclaimerSettings.showDate}
                        onChange={(e) => onDisclaimerSettingsChange({
                          ...disclaimerSettings,
                          showDate: e.target.checked
                        })}
                        className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-teal-500 
                          focus:ring-teal-500 focus:ring-offset-slate-900"
                      />
                      <Calendar size={16} className="text-slate-400" />
                      <span className="text-sm text-slate-300">Show Date</span>
                    </label>

                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={disclaimerSettings.showBranding}
                        onChange={(e) => onDisclaimerSettingsChange({
                          ...disclaimerSettings,
                          showBranding: e.target.checked
                        })}
                        className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-teal-500 
                          focus:ring-teal-500 focus:ring-offset-slate-900"
                      />
                      <Building2 size={16} className="text-slate-400" />
                      <span className="text-sm text-slate-300">Show Branding</span>
                    </label>
                  </div>

                  {/* Branding text */}
                  {disclaimerSettings.showBranding && (
                    <div>
                      <label className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-2 block">
                        Branding Text
                      </label>
                      <input
                        type="text"
                        value={disclaimerSettings.brandingText || ''}
                        onChange={(e) => onDisclaimerSettingsChange({
                          ...disclaimerSettings,
                          brandingText: e.target.value || undefined
                        })}
                        placeholder="e.g., Your Company Name"
                        className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg
                          text-white placeholder-slate-500 focus:outline-none focus:border-teal-500"
                      />
                    </div>
                  )}

                  {/* Disclaimer text */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-xs font-medium text-slate-400 uppercase tracking-wider">
                        Disclaimer Text
                      </label>
                      <button
                        onClick={() => onDisclaimerSettingsChange({
                          ...disclaimerSettings,
                          disclaimerText: DEFAULT_DISCLAIMER_TEXT
                        })}
                        className="text-xs text-teal-400 hover:text-teal-300"
                      >
                        Reset to Default
                      </button>
                    </div>
                    <textarea
                      value={disclaimerSettings.disclaimerText}
                      onChange={(e) => onDisclaimerSettingsChange({
                        ...disclaimerSettings,
                        disclaimerText: e.target.value
                      })}
                      rows={4}
                      className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg
                        text-white text-sm placeholder-slate-500 focus:outline-none focus:border-teal-500
                        resize-none font-mono"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Use <code className="text-teal-400">{'{totalArea}'}</code> to insert the calculated area
                    </p>
                  </div>

                  {/* Preview */}
                  <div>
                    <label className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-2 block">
                      Footer Preview
                    </label>
                    <div className="p-4 bg-white rounded-lg border border-slate-600">
                      <div className="border-t border-slate-300 pt-4 text-slate-800">
                        {disclaimerSettings.propertyName && (
                          <div className="font-semibold text-lg mb-2">
                            {disclaimerSettings.propertyName}
                          </div>
                        )}
                        <div className="text-xs whitespace-pre-wrap leading-relaxed">
                          {previewDisclaimer}
                        </div>
                        <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-200">
                          {disclaimerSettings.showDate && (
                            <span className="text-xs text-slate-500">
                              date: {new Date().toLocaleDateString('en-GB')}
                            </span>
                          )}
                          {disclaimerSettings.showBranding && disclaimerSettings.brandingText && (
                            <span className="text-xs font-medium text-slate-600">
                              {disclaimerSettings.brandingText}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {activeTab === 'format' && (
            <div className="space-y-6">
              {/* Format selection */}
              <div>
                <label className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-3 block">
                  Image Format
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {(['png', 'jpeg'] as const).map((format) => (
                    <button
                      key={format}
                      onClick={() => handleFormatChange(format)}
                      className={`p-4 rounded-xl border-2 transition-all text-left
                        ${exportConfig.format === format 
                          ? 'border-teal-500 bg-teal-900/30' 
                          : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'
                        }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className={`font-semibold uppercase ${
                          exportConfig.format === format ? 'text-teal-300' : 'text-slate-300'
                        }`}>
                          {format}
                        </span>
                        {exportConfig.format === format && (
                          <Check size={16} className="text-teal-400" />
                        )}
                      </div>
                      <p className="text-xs text-slate-400">
                        {format === 'png' 
                          ? 'Lossless, larger file size, transparent support'
                          : 'Smaller file size, no transparency'
                        }
                      </p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Quality slider (JPEG only) */}
              {exportConfig.format === 'jpeg' && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs font-medium text-slate-400 uppercase tracking-wider">
                      Quality
                    </label>
                    <span className="text-sm text-slate-300">
                      {Math.round(exportConfig.quality * 100)}%
                    </span>
                  </div>
                  <input
                    type="range"
                    min="0.5"
                    max="1"
                    step="0.05"
                    value={exportConfig.quality}
                    onChange={(e) => onExportConfigChange({
                      ...exportConfig,
                      quality: Number(e.target.value)
                    })}
                    className="w-full h-2 bg-slate-700 rounded-full appearance-none cursor-pointer
                      [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 
                      [&::-webkit-slider-thumb]:bg-teal-500 [&::-webkit-slider-thumb]:rounded-full"
                  />
                </div>
              )}

              {/* Resolution scale */}
              <div>
                <label className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-3 block">
                  Resolution
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {[1, 2, 3].map((scale) => (
                    <button
                      key={scale}
                      onClick={() => handleScaleChange(scale)}
                      className={`p-3 rounded-xl border-2 transition-all text-center
                        ${exportConfig.scale === scale 
                          ? 'border-teal-500 bg-teal-900/30' 
                          : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'
                        }`}
                    >
                      <div className={`font-semibold ${
                        exportConfig.scale === scale ? 'text-teal-300' : 'text-slate-300'
                      }`}>
                        {scale}x
                      </div>
                      <div className="text-xs text-slate-400 mt-1">
                        {scale === 1 ? 'Standard' : scale === 2 ? 'High Res' : 'Ultra'}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Padding */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Border Padding
                  </label>
                  <span className="text-sm text-slate-300">{exportConfig.padding}px</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="10"
                  value={exportConfig.padding}
                  onChange={(e) => onExportConfigChange({
                    ...exportConfig,
                    padding: Number(e.target.value)
                  })}
                  className="w-full h-2 bg-slate-700 rounded-full appearance-none cursor-pointer
                    [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 
                    [&::-webkit-slider-thumb]:bg-teal-500 [&::-webkit-slider-thumb]:rounded-full"
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-700 bg-slate-800/30 shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-300 hover:text-white 
              bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              onExport();
              onClose();
            }}
            className="flex items-center gap-2 px-5 py-2 text-sm font-medium text-white 
              bg-teal-600 hover:bg-teal-500 rounded-lg transition-colors"
          >
            <Download size={16} />
            Export Floor Plan
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExportSettingsModal;
